import { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { showNotification } from 'sonata-react-components';

import { groupingMessages } from '../components/groupingMessages';
import { areaCodeOf, selectableLevels } from '../domain/cartographyGrouping';
import { getAreaNames, getCountryLevelsConfig, getPointsTags } from '../services/cartographyGrouping.service';

import type { CartographyLevel } from '../domain/cartographyGrouping';
import type { GroupChange } from '../domain/grouping.types';
import type { CartographyTags, LatLng } from '../services/cartographyGrouping.service';
import type { GroupingItem } from './useGroupingItems';

export interface UseCartographyGroupingInput {
  /** CCA3 shared by every selected item; nothing is fetched without it. */
  country: string | null;
  /** Fetch only once the user switches to the cartographic mode. */
  enabled: boolean;
  items: GroupingItem[];
}

export interface UseCartographyGroupingResult {
  /** Resolves the area names and returns the `createCartography` change; rejects when the request fails. */
  buildChange: (level: string) => Promise<GroupChange>;
  /** Selected items, venues completed with the tags fetched for their position. */
  items: GroupingItem[];
  levels: CartographyLevel[];
  loading: boolean;
}

type ItemToTag = GroupingItem & { latlng: LatLng };

const needsTags = (item: GroupingItem): item is ItemToTag => !item.tags && item.latlng !== undefined;

/** Async side of cartographic grouping: country levels, missing venue tags and area names (network only, no writes). */
export function useCartographyGrouping({
  country,
  enabled,
  items
}: UseCartographyGroupingInput): UseCartographyGroupingResult {
  const { formatMessage } = useIntl();
  const [levels, setLevels] = useState<CartographyLevel[]>([]);
  const [fetchedTags, setFetchedTags] = useState<Record<string, CartographyTags>>({});
  const [pending, setPending] = useState(0);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const active = enabled && country !== null;
  const toTagKey = items
    .filter(needsTags)
    .map(item => item.id)
    .join(',');

  // biome-ignore lint/correctness/useExhaustiveDependencies: items are read through a ref; `toTagKey` (ids of the items to tag) triggers the refetch so a new array with the same items does not
  useEffect(() => {
    if (!active || !country) {
      return undefined;
    }
    let cancelled = false;
    const track = <T>(promise: Promise<T>, onDone: (value: T) => void) => {
      setPending(count => count + 1);
      promise
        .then(value => !cancelled && onDone(value))
        .catch(() => !cancelled && showNotification('error', formatMessage(groupingMessages.cartographyLoadError)))
        .finally(() => setPending(count => count - 1));
    };
    track(getCountryLevelsConfig(country), config => setLevels(selectableLevels(config)));
    const toTag = itemsRef.current.filter(needsTags);
    if (toTag.length) {
      track(
        getPointsTags(
          country,
          toTag.map(item => item.latlng)
        ),
        tags => setFetchedTags(Object.fromEntries(toTag.map((item, index) => [item.id, tags[index] ?? {}])))
      );
    }
    return () => {
      cancelled = true;
    };
  }, [active, country, formatMessage, toTagKey]);

  const taggedItems = useMemo(
    () => items.map(item => (fetchedTags[item.id] ? { ...item, tags: fetchedTags[item.id] } : item)),
    [items, fetchedTags]
  );

  const buildChange = async (level: string): Promise<GroupChange> => {
    if (!country) {
      throw new Error('Cartographic groups need a single country');
    }
    const assignments = taggedItems.map(item => ({ areaCode: areaCodeOf(item, level), itemId: item.id }));
    const areaCodes = [...new Set(assignments.flatMap(({ areaCode }) => (areaCode ? [areaCode] : [])))];
    const areaNames = await getAreaNames(country, level, areaCodes);
    return { areaNames, assignments, country, level, type: 'createCartography' };
  };

  return { buildChange, items: taggedItems, levels, loading: active && pending > 0 };
}
